import { DataHandlingSection, DocumentsSection, OverviewSection, RiskExposureSection } from "@/modules/agent/components/VendorAssessmentIntakeForm/sub-components";
import ConfirmSubmissionModal from "@/modules/agent/components/VendorEmail/ConfirmSubmissionModal";
import { AllowedChildFields, AllowedParentFields } from "@/modules/agent/types/vendorIntakeForm";
import GuestSidebar from "@/modules/app/components/sidebar/GuestSidebar";
import { useAdvancedState } from "@/shared/hooks/useAdvancedState";
import { addNotification } from "@/shared/states/notification";
import { UploadedFile } from "@/shared/types/file-upload";
import { cn } from "@/shared/utils/classname-merger";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@nextui-org/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { thirdPartyEvaluationFormSchema } from "../constants/form-validation";
import { useSubmitVendorIntakeForm, validateThirdPartyVendorFormToken } from "../requests";
import ThirdPartyFormExpiredScreen from "./ExpiredScreen";
import ThirdPartyFormSubmittedSuccessfullyScreen from "./SuccessScreen";

export const ThirdPartyVendorLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <main className="h-screen relative w-screen flex">
      <section
        className={cn("flex-[5]", "transition-all duration-300 ease-in-out",
        )}
      >
        <GuestSidebar />
      </section>
      <section
        className={cn("flex-[95]", "overflow-hidden overflow-y-auto p-4 ",
        )}
      >
        <div className="overflow-hidden rounded-lg border-none w-full h-full">
          {children}
        </div>
      </section>
    </main>
  );
}

const VendorIntakeForm = () => {

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const vendorToken = queryParams.get('token') as string;

  const {
    state: { isLoading, data, isSessionExpired, isAlreadySubmitted, source_urls, isConfirmModalOpen },
    updateState,
    toggleField
  } = useAdvancedState<any>({
    isLoading: true,
    data: {},
    isSessionExpired: false, isAlreadySubmitted: false,
    source_urls: [],
    isConfirmModalOpen: false,
  });

  const fetchVendorFormDetails = async (token: string) => {
    try {
      const response = await validateThirdPartyVendorFormToken(token);
      updateState({
        data: response,
        isSessionExpired: !response.success
      })

      return response
    } catch (error) {
      if ((error as any)?.status === 403) {
        // return Promise.reject('Intake form already submitted');
        updateState({
          isAlreadySubmitted: true
        })
      } else {
        updateState({
          isSessionExpired: true
        })
      }

    } finally {
      updateState({
        isLoading: false
      })
    }
  }

  useEffect(() => {
    fetchVendorFormDetails(vendorToken as string)
  }, [vendorToken]);

  const { questionnaire_required, questionnaire_url: assessorQuestionnaireUrl = "", vendor_name } = data || {};

  const [isThereFileUploadError, setIsThereFileUploadError] = useState(true);

  const { mutate: submitVendorDetails, isIdle } = useSubmitVendorIntakeForm()

  const { handleSubmit, watch, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(thirdPartyEvaluationFormSchema),
    defaultValues: {
      vendor_usage_description: "",
      business_criticality: "",
      system_access_level: "",
      vendor_submitted_files: [],
      questionnaire_url: "",
      isQuestionnaireRequired: false,
      collected_data_types: {
        data_types: [],
        other_data_types: "",
      },
      data_access_scope: {
        scope: [],
        other_scope: "",
      },
      nature_of_relationship: "",
      specify_relationship_type_other: "",
      vendor_ai_ml_usage: "No",
    }
  });

  useEffect(() => {
    if (assessorQuestionnaireUrl) {
      setValue("isQuestionnaireRequired", true)
    }
  }, [assessorQuestionnaireUrl])

  if (isLoading) {
    return null;
  }


  if (isSessionExpired) {
    // Currently hard coded to show Expired Page when no token is passed. 
    // But this expired page should be visible when API shows Session is Discarded
    return <ThirdPartyFormExpiredScreen contact_email={data.data?.contact_email} />
  }

  if (isAlreadySubmitted) {
    // Currently hard coded 
    // Succcess screen will be visible when the Form has been submitted successfully.
    return (
      <ThirdPartyFormSubmittedSuccessfullyScreen />
    )
  }

  const formData = watch();

  const handleVendorUsageDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue("vendor_usage_description", e.target.value);
  }

  const handleSwitchChange = (switchValue: string) => {
    setValue("vendor_ai_ml_usage", switchValue);
  }

  const handleSelectChange = (field: string, selectedItems: string | string[] | Set<string>) => {
    let value;
    if (
      field === 'business_criticality' ||
      field === 'system_access_level' ||
      field === 'nature_of_relationship'
    ) {
      value = Array.from(selectedItems)[0];
    } else {
      value = Array.isArray(selectedItems)
        ? selectedItems
        : Array.from(selectedItems);
    }

    if (!field.includes('.')) {
      // flat field, no nesting
      setValue(field as any, value);
      return;
    } else {
      // nested field
      const splitFields = field.split('.');

      if (splitFields.length !== 2) {
        throw new Error(
          'Invalid field name. Only two levels of nesting are allowed.'
        );
      }

      const parentField = splitFields[0] as AllowedParentFields;
      const childField = splitFields[1] as AllowedChildFields;


      setValue(parentField, { ...formData[parentField], [childField]: value });
    }

  }

  const handleOtherValueChange = (field: string, value: string) => {
    if (!field.includes(".")) {
      // flat field, no nesting
      setValue(field as any, value);
      return;
    } else {
      // nested field
      const splitFields = field.split(".");

      if (splitFields.length !== 2) {
        throw new Error(
          "Invalid field name. Only two levels of nesting are allowed.",
        );
      }

      const parentField = splitFields[0] as AllowedParentFields;
      const childField = splitFields[1] as AllowedChildFields;

      setValue(parentField, { ...formData[parentField], [childField]: value });
    }
  }

  const updateQuestionnaireUrl = (files: UploadedFile[]) => {
    const fileUrls = files.map((file) => file.file.name);
    const signedUrls = files.map((file) => file.signedUrl || "");
    // Only update if there are actual changes
    setValue("questionnaire_url", signedUrls[0]);
  }

  const updateSignedUrl = (files: UploadedFile[]) => {
    const fileUrls = files.map((file) => file.file.name);
    const signedUrls = files.map((file) => file.signedUrl || "");
    // Only update if there are actual changes
    if (
      JSON.stringify(fileUrls) !==
      JSON.stringify(formData.vendor_submitted_files)
    ) {
      setValue("vendor_submitted_files", fileUrls);
      updateState({ source_urls: signedUrls });
    }
  }

  const confirmSubmission = () => {
    const payload = {
      vendor_intake_form: { ...watch(), vendor_name },
      source_urls,
      token: vendorToken as string
    } as any

    if (assessorQuestionnaireUrl) {
      payload.questionnaire_url = data.questionnaire_url
    }
    submitVendorDetails(payload as any, {
      onSuccess: (successResponse) => {
        if (successResponse.success) {
          updateState({
            isAlreadySubmitted: true,
          })
        } else {
          addNotification({
            message: successResponse.message,
            type: "error",
          });
        }
      },
      onSettled: () => {
        toggleField("isConfirmModalOpen");
      },
      onError: () => {
        addNotification({
          message: "Something went wrong. Please contact support.",
          type: "error",
        });
      }
    })
  }

  const handleSubmitVendorData = () => {
    toggleField("isConfirmModalOpen");
  }

  const handleErrors = (errors: any) => {
    console.log("errors", errors, watch());
    addNotification({
      message: "Please provide all required information.",
      type: "error",
    });
  }




  return (
    <ThirdPartyVendorLayout>
      <form onSubmit={handleSubmit(handleSubmitVendorData, handleErrors)}>

        <div className="w-full flex flex-col">
          <div className="grow overflow-hidden rounded-xl">

            <div className="w-full h-screen bg-[#FAFAFA] bg-opacity-90 flex flex-col items-center">
              <div className="flex px-6 py-6 justify-between items-start border-b w-full bg-[#FEFEFE]">
                <div className="flex flex-col gap-2">
                  <span className="font-semibold text-2xl">
                    Evaluation Form from {vendor_name}
                  </span>
                  <span className="text-sm text-[#71717A]">
                    Submit the information below to begin your onboarding. For questions, please contact <a href={`mailto:${data.contact_email}`} className="text-blue-600">{data.contact_email}</a>.
                  </span>
                </div>

                <div className="flex items-start gap-5">
                  <div className="flex items-center h-7 gap-1">

                  </div>
                  <Button
                    color="primary"
                    type="submit"
                    className="rounded-md text-sm leading-5 h-7 w-[144px]"
                  >
                    Submit
                  </Button>
                </div>
              </div>
              <div className="px-6 py-4 w-full h-screen overflow-auto">
                <div className="bg-[#FEFEFE] w-full rounded-lg p-6 flex flex-col gap-12 overflow-y-auto mb-8">
                  {assessorQuestionnaireUrl && <DocumentsSection
                    maxFiles={1}
                    questionnaireDocuments={assessorQuestionnaireUrl}
                    title="Questionnaire"
                    description="Please complete the attached questionnaire and upload it below."
                    updateSignedUrl={updateQuestionnaireUrl}
                    setIsThereFileUploadError={setIsThereFileUploadError}
                    initialFiles={formData?.vendor_submitted_files?.map(
                      (fileName, index) => ({
                        id: `file-${index}`,
                        file: new File([], fileName),
                        preview: "",
                        status: "success",
                        progressBar: false,
                        type: "",
                        signedUrl: ""
                      })
                    )}
                    allowedFileTypes={[
                      "text/csv",
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      "application/vnd.ms-excel"
                    ]}
                    maxSizeMB={10}
                  />}
                  <DocumentsSection
                    updateSignedUrl={updateSignedUrl}
                    setIsThereFileUploadError={setIsThereFileUploadError}
                    initialFiles={formData?.vendor_submitted_files?.map(
                      (fileName, index) => ({
                        id: `file-${index}`,
                        file: new File([], fileName),
                        preview: "",
                        status: "success",
                        progressBar: false,
                        type: "",
                        signedUrl: ""
                      })
                    )}
                  />
                  <OverviewSection
                    formData={formData as any}
                    handleVendorUsageDescriptionChange={
                      handleVendorUsageDescriptionChange
                    }
                    handleSwitchChange={handleSwitchChange}
                    handleSelectChange={handleSelectChange}
                    handleOtherValueChange={handleOtherValueChange}
                  />

                  <DataHandlingSection
                    formData={formData as any}
                    handleSelectChange={handleSelectChange}
                    handleOtherValueChange={handleOtherValueChange}
                  />
                  <RiskExposureSection
                    formData={formData}
                    handleSelectChange={handleSelectChange}
                  />

                </div>
              </div>
            </div>

          </div>
        </div>
        {isConfirmModalOpen && (
          <ConfirmSubmissionModal
            isOpen
            toggleModal={() => toggleField("isConfirmModalOpen")}
            sessionName={""}
            onConfirm={confirmSubmission}
            isLoading={!isIdle}
          />
        )}
      </form>

    </ThirdPartyVendorLayout>
  );
}


export default VendorIntakeForm;